import * as THREE from 'three';
import type { Group } from 'three';

import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';

import * as globalStores from '../../stores/global';
import { disposeCartographerObject3D } from './lib/disposeCartographerObject3D';
import {
  CARTOGRAPHER_HISTORY_LIMIT,
  type CartographerHistoryEntry,
  type CartographerHistoryItem,
  type CartographerHistoryKind,
  type CartographerHistorySnapshot,
  appendCartographerHistoryEntry,
  applyIkSnapshotToSession,
  areCartographerHistorySnapshotsEqual,
  captureCartographerHistorySnapshot,
  cloneMappingResult,
  clonePoseLocals,
  createCartographerHistoryEntry,
  restoreBonePropertiesSnapshot,
  toCartographerHistoryItem,
} from './lib/history/cartographerHistory';
import {
  type ImportWarningDetail,
  formatImportWarningMessage,
} from './lib/io/importWarnings';
import {
  JOINT_OFFSETS_IO_KEY,
  getJointOffsetRotationEntries,
} from './lib/io/jointOffsets';
import { exportMappingJson, importMappingJson } from './lib/io/mappingIO';
import {
  captureCartographerLegModeSnapshot,
  type CartographerLegModeSnapshot,
} from './lib/mapping/legMode';
import {
  type ManualMappingDraft,
  type ManualRoleChainSyncCallbacks,
  assignRoleToDraft,
  clearBoneAssignmentsFromDraft,
  clearRolesForBoneFromDraft,
  syncChainAssignmentDraft,
  syncRoleAssignmentDraft,
} from './lib/mapping/manualRoleChainSync';
import type { OutlinerHierarchy } from './lib/outliner';
import { MappingResult } from './lib/recognize/config';
import { recognizeSkeleton } from './lib/recognize/recognizer';
import type { CharacterAxis } from './lib/schema/characterAxes';
import { ChainMapping } from './lib/schema/mapping';
import {
  type ChainSolverProperties,
  areSolverPropertiesEqual,
  getEffectiveSolverType,
  normalizeSolverProperties,
  normalizeSolverType,
} from './lib/schema/solver';
import { buildShortestBonePathRefs } from './lib/skeleton/bonePath';
import {
  type BonePoseTransform,
  writeBonePoseLocalQuaternion,
  writeBonePoseTransform,
} from './lib/skeleton/boneTransforms';
import type { BoneProperties, Skeleton } from './lib/skeleton/core';
import {
  eulerDegreesFromQuaternion,
  normalizeQuaternion,
  quaternionFromEulerDegrees,
} from './lib/skeleton/rotationService';
import {
  type EffectorId,
  IKSession,
  buildIKSession,
} from './lib/solvers/ikSession';

type Deps = { global: typeof globalStores };

export type ChainSummary = {
  name: string;
  boneNames: string[];
  startBone: string;
  endBone: string;
  confidence: number;
};

export type { ImportWarningDetail } from './lib/io/importWarnings';

export type LoadedRenderAssetInfo = {
  lodPolicy: 'highest' | null;
  selectedLodLabel: string | null;
  skippedLodLabels: string[];
  skippedLodMeshNames: string[];
  morphTargetsIgnored: number;
};

export type ViewerVisibility = {
  showBones: boolean;
  showExcludedBones: boolean;
  showUnassignedBones: boolean;
  showMesh: boolean;
  showGrid: boolean;
  showChains: boolean;
  showBoneNames: boolean;
  showRoles: boolean;
  showProperties: boolean;
  showShadow: boolean;
  /** When true, mesh uses Cartographer gray `MeshStandardMaterial` instead of authored materials. */
  useDefaultShader: boolean;
  showSelectedBoneAxes: boolean;
};

export type ToolbarMenuMode = 'none' | 'visibility' | 'settings';
export type ViewerToolMode = 'select' | 'rotate' | 'properties';
export type RotateSpaceMode = 'local' | 'gimbal';
export type PropertiesToolMode = 'limits' | 'angle-bias' | 'offset';
export type GizmoRotationAxis = 'x' | 'y' | 'z';
export type BoneColorPreset = 'white' | 'grey' | 'charcoal' | 'black';
export type CartographerSelection =
  | {
      kind: 'bone';
      boneIndex: number;
    }
  | {
      kind: 'transform';
      nodeId: string;
    }
  | null;
export type CartographerPoseSnapshot = THREE.Matrix4[];

/** `0` keeps the viewer grid sized automatically from the current scene bounds. */
export const CARTOGRAPHER_AUTO_GRID_CELL_SIZE = 0;

const RENDER_SCALE_MIN = 0.1;
const RENDER_SCALE_MAX = 10;
const QUATERNION_DOT_EPSILON = 1e-6;
const GIMBAL_DELTA_EPSILON_DEGREES = 1e-4;
const GIMBAL_AXIS_INDEX: Record<GizmoRotationAxis, number> = {
  x: 0,
  y: 1,
  z: 2,
};
function readQuaternionFromMatrix(matrix: THREE.Matrix4): THREE.Quaternion {
  const quaternion = new THREE.Quaternion();
  matrix.decompose(new THREE.Vector3(), quaternion, new THREE.Vector3());
  return normalizeQuaternion(quaternion);
}

function areQuaternionsNearlyEqual(
  a: THREE.Quaternion,
  b: THREE.Quaternion,
): boolean {
  const safeDot = Math.min(1, Math.max(-1, a.dot(b)));
  return 1 - Math.abs(safeDot) <= QUATERNION_DOT_EPSILON;
}

function areStringArraysEqual(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function clampRenderScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  const clamped = Math.min(RENDER_SCALE_MAX, Math.max(RENDER_SCALE_MIN, value));
  return Number(clamped.toFixed(2));
}

function normalizeOffsetDegrees(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) < 1e-4) return 0;
  const wrapped = THREE.MathUtils.euclideanModulo(value + 180, 360) - 180;
  return Math.abs(wrapped) < 1e-4 ? 0 : wrapped;
}

export function getSelectedBoneIndex(
  selection: CartographerSelection,
): number | null {
  return selection?.kind === 'bone' ? selection.boneIndex : null;
}

export function getSelectedOutlinerNodeId(
  selection: CartographerSelection,
  hierarchy: OutlinerHierarchy | null,
): string | null {
  if (selection === null) return null;
  if (selection.kind === 'transform') {
    return hierarchy?.nodesById[selection.nodeId] ? selection.nodeId : null;
  }
  return hierarchy?.boneNodeIdByIndex[selection.boneIndex] ?? null;
}

const defaultVisibility: ViewerVisibility = {
  showBones: true,
  showExcludedBones: true,
  showUnassignedBones: true,
  showMesh: true,
  showGrid: true,
  showChains: true,
  showBoneNames: false,
  showRoles: false,
  showProperties: true,
  showShadow: true,
  useDefaultShader: false,
  showSelectedBoneAxes: true,
};

export type CartographerState = {
  fileName: string | null;
  fileVersion: number;
  /** Mapping revision counter used for unsaved-change tracking in the editor. */
  mappingVersion: number;
  /** Bone-properties revision counter used to refresh non-proxy bone property UIs. */
  bonePropertiesVersion: number;
  hasGltfScene: boolean;
  hasSkeleton: boolean;
  hasMapping: boolean;
  renderAssetInfo: LoadedRenderAssetInfo | null;
  chainSummaries: ChainSummary[];
  presetName: string | null;
  loading: boolean;
  error: string | null;
  warning: string | null;
  visibility: ViewerVisibility;
  toolbarMenuMode: ToolbarMenuMode;
  viewerToolMode: ViewerToolMode;
  rotateSpaceMode: RotateSpaceMode;
  propertiesToolMode: PropertiesToolMode;
  /** Positive values force a fixed spacing; `<= 0` enables adaptive sizing. */
  gridCellSize: number;
  boneColorPreset: BoneColorPreset;
  boneRadiusScale: number;
  chainThicknessScale: number;
  manipulatorScale: number;
  cameraFollowMotion: boolean;
  /** Canonical Cartographer selection shared across viewer, outliner, and inspector. */
  selection: CartographerSelection;
  /** Incremented whenever the active bone selection changes, including re-selecting the same bone. */
  selectionVersion: number;
  /** Incremented whenever a pose matrix update should re-render the viewer. */
  poseVersion: number;
  excludedBoneNames: string[];
  importWarningDetails: ImportWarningDetail[];
  /** Whether IK mode is active. */
  ikEnabled: boolean;
  /** IK solve version counter (bumped each time solve runs so viewer re-renders). */
  ikSolveVersion: number;
  /** List of available effector IDs. */
  ikEffectorIds: EffectorId[];
  historyItems: CartographerHistoryItem[];
  historyIndex: number;
};

const initialState: CartographerState = {
  fileName: null,
  fileVersion: 0,
  mappingVersion: 0,
  bonePropertiesVersion: 0,
  hasGltfScene: false,
  hasSkeleton: false,
  hasMapping: false,
  renderAssetInfo: null,
  chainSummaries: [],
  presetName: null,
  loading: false,
  error: null,
  warning: null,
  visibility: { ...defaultVisibility },
  toolbarMenuMode: 'none',
  viewerToolMode: 'select',
  rotateSpaceMode: 'local',
  propertiesToolMode: 'limits',
  gridCellSize: CARTOGRAPHER_AUTO_GRID_CELL_SIZE,
  boneColorPreset: 'charcoal',
  boneRadiusScale: 1,
  chainThicknessScale: 1,
  manipulatorScale: 1,
  cameraFollowMotion: false,
  selection: null,
  selectionVersion: 0,
  poseVersion: 0,
  excludedBoneNames: [],
  importWarningDetails: [],
  ikEnabled: false,
  ikSolveVersion: 0,
  ikEffectorIds: [],
  historyItems: [],
  historyIndex: -1,
};

class CartographerStore extends BaseStore<CartographerState, Deps> {
  private _file: File | null = null;
  private _gltfScene: Group | null = null;
  private _skeleton: Skeleton | null = null;
  private _outlinerHierarchy: OutlinerHierarchy | null = null;
  private _mappingResult: MappingResult | null = null;
  private _recognitionWeightsInfo: Map<number, boolean> | null = null;
  private _ikSession: IKSession | null = null;
  private _jointOffsetPreviewActive = false;
  private _historyEntries: CartographerHistoryEntry[] = [];
  private _historySavedEntryId: string | null = null;
  private _historyRestoreInProgress = false;
  private _openHistoryTransaction: {
    label: string;
    kind: CartographerHistoryKind;
    before: CartographerHistorySnapshot;
  } | null = null;
  private _nextMappingVersion = 1;
  private _nextBonePropertiesVersion = 1;

  constructor(deps: Deps) {
    super(initialState, deps);
  }

  init() {}

  private resetHistoryState() {
    this._historyEntries = [];
    this._historySavedEntryId = null;
    this._historyRestoreInProgress = false;
    this._openHistoryTransaction = null;
    this._nextMappingVersion = 1;
    this._nextBonePropertiesVersion = 1;
    this.state.historyItems = [];
    this.state.historyIndex = -1;
  }

  private syncHistoryState(historyIndex = this.state.historyIndex) {
    if (
      this._historySavedEntryId &&
      !this._historyEntries.some(
        (entry) => entry.id === this._historySavedEntryId,
      )
    ) {
      this._historySavedEntryId = null;
    }

    this.state.historyIndex =
      this._historyEntries.length === 0
        ? -1
        : Math.max(0, Math.min(historyIndex, this._historyEntries.length - 1));
    this.state.historyItems = this._historyEntries.map((entry) =>
      toCartographerHistoryItem(entry, this._historySavedEntryId),
    );
  }

  private setHistoryEntries(
    historyEntries: CartographerHistoryEntry[],
    historyIndex: number,
  ) {
    this._historyEntries = historyEntries;
    this.syncHistoryState(historyIndex);
  }

  private takeNextMappingVersion() {
    const nextVersion = this._nextMappingVersion;
    this._nextMappingVersion += 1;
    return nextVersion;
  }

  private takeNextBonePropertiesVersion() {
    const nextVersion = this._nextBonePropertiesVersion;
    this._nextBonePropertiesVersion += 1;
    return nextVersion;
  }

  private ensureRevisionCounters(snapshot: {
    mappingVersion: number;
    bonePropertiesVersion: number;
  }) {
    this._nextMappingVersion = Math.max(
      this._nextMappingVersion,
      snapshot.mappingVersion + 1,
    );
    this._nextBonePropertiesVersion = Math.max(
      this._nextBonePropertiesVersion,
      snapshot.bonePropertiesVersion + 1,
    );
  }

  private assignMappingVersion(version: number, source: string) {
    const previousMappingVersion = this.state.mappingVersion;
    this.state.mappingVersion = version;
    this.ensureRevisionCounters({
      mappingVersion: version,
      bonePropertiesVersion: this.state.bonePropertiesVersion,
    });
    this.logMappingVersionChange(source, previousMappingVersion);
  }

  private bumpMappingVersion(source: string) {
    this.assignMappingVersion(this.takeNextMappingVersion(), source);
  }

  private bumpBonePropertiesVersion() {
    this.state.bonePropertiesVersion = this.takeNextBonePropertiesVersion();
  }

  private captureHistorySnapshot(): CartographerHistorySnapshot {
    return captureCartographerHistorySnapshot({
      mapping: this._mappingResult,
      skeleton: this._skeleton,
      excludedBoneNames: this.state.excludedBoneNames,
      ikEnabled: this.state.ikEnabled,
      ikSession: this._ikSession,
      mappingVersion: this.state.mappingVersion,
      bonePropertiesVersion: this.state.bonePropertiesVersion,
    });
  }

  private pushHistoryEntry(
    label: string,
    kind: CartographerHistoryKind,
    snapshot: CartographerHistorySnapshot,
  ) {
    const next = appendCartographerHistoryEntry(
      this._historyEntries,
      this.state.historyIndex,
      createCartographerHistoryEntry({
        kind,
        label,
        snapshot,
      }),
      CARTOGRAPHER_HISTORY_LIMIT,
    );
    this.setHistoryEntries(next.history, next.historyIndex);
  }

  private pushHistoryEntryIfChanged(
    before: CartographerHistorySnapshot,
    label: string,
    kind: CartographerHistoryKind,
  ) {
    const after = this.captureHistorySnapshot();
    if (areCartographerHistorySnapshotsEqual(before, after)) {
      return false;
    }
    this.pushHistoryEntry(label, kind, after);
    return true;
  }

  captureHistoryBaseline(label = 'Initial state') {
    if (this._historyEntries.length > 0) return;
    this.setHistoryEntries(
      [
        createCartographerHistoryEntry({
          kind: 'baseline',
          label,
          snapshot: this.captureHistorySnapshot(),
        }),
      ],
      0,
    );
  }

  markCurrentHistoryEntrySaved() {
    const activeEntry = this._historyEntries[this.state.historyIndex];
    this._historySavedEntryId = activeEntry?.id ?? null;
    this.syncHistoryState();
  }

  recordHistory<T>(
    label: string,
    kind: CartographerHistoryKind,
    mutator: () => T,
  ): T {
    if (this._historyRestoreInProgress || this._openHistoryTransaction) {
      return mutator();
    }

    this.captureHistoryBaseline();
    const before = this.captureHistorySnapshot();
    const result = mutator();
    this.pushHistoryEntryIfChanged(before, label, kind);
    return result;
  }

  beginHistoryTransaction(label: string, kind: CartographerHistoryKind) {
    if (this._historyRestoreInProgress || this._openHistoryTransaction) return;
    this.captureHistoryBaseline();
    this._openHistoryTransaction = {
      label,
      kind,
      before: this.captureHistorySnapshot(),
    };
  }

  commitHistoryTransaction() {
    if (!this._openHistoryTransaction) return;
    const transaction = this._openHistoryTransaction;
    this._openHistoryTransaction = null;
    this.pushHistoryEntryIfChanged(
      transaction.before,
      transaction.label,
      transaction.kind,
    );
  }

  cancelHistoryTransaction() {
    this._openHistoryTransaction = null;
  }

  captureLegModeSnapshot(): CartographerLegModeSnapshot {
    return captureCartographerLegModeSnapshot(this._mappingResult);
  }

  undo() {
    if (this.state.historyIndex <= 0) return;
    this.jumpToHistory(this.state.historyIndex - 1);
  }

  redo() {
    if (
      this.state.historyIndex < 0 ||
      this.state.historyIndex >= this._historyEntries.length - 1
    ) {
      return;
    }
    this.jumpToHistory(this.state.historyIndex + 1);
  }

  jumpToHistory(index: number) {
    if (index === this.state.historyIndex) return;
    if (index < 0 || index >= this._historyEntries.length) return;

    const targetEntry = this._historyEntries[index];
    this.restoreHistorySnapshot(targetEntry.snapshot);
    this.syncHistoryState(index);
  }

  private restoreHistorySnapshot(snapshot: CartographerHistorySnapshot) {
    this._historyRestoreInProgress = true;
    try {
      this.setMappingResultInternal(cloneMappingResult(snapshot.mapping), {
        source: 'restoreHistory',
        mappingVersion: snapshot.mappingVersion,
      });
      this.state.bonePropertiesVersion = snapshot.bonePropertiesVersion;
      this.ensureRevisionCounters(snapshot);
      restoreBonePropertiesSnapshot(
        this._skeleton,
        snapshot.bonePropertiesByBoneRef,
      );
      const poseLocals = clonePoseLocals(snapshot.poseLocals);
      if (poseLocals) {
        this.restorePoseSnapshot(poseLocals);
      } else if (this._skeleton) {
        this._skeleton.resetPoses();
        this.state.poseVersion = this.state.poseVersion + 1;
      }

      if (snapshot.mapping === null) {
        this.state.excludedBoneNames = [...snapshot.excludedBoneNames];
      }

      if (snapshot.ik.enabled && this._ikSession) {
        applyIkSnapshotToSession(this._ikSession, snapshot.ik);
        this.state.ikEnabled = true;
        this.state.ikEffectorIds = [...this._ikSession.effectors.keys()];
      } else {
        this._ikSession = null;
        this.state.ikEnabled = false;
        this.state.ikEffectorIds = [];
      }
      this.state.ikSolveVersion = this.state.ikSolveVersion + 1;
      if (this._jointOffsetPreviewActive) {
        this.applyJointOffsetPreviewPose();
      }
    } finally {
      this._historyRestoreInProgress = false;
    }
  }

  reset() {
    const previousScene = this._gltfScene;
    this._file = null;
    this._gltfScene = null;
    this._skeleton = null;
    this._outlinerHierarchy = null;
    this._mappingResult = null;
    this._recognitionWeightsInfo = null;
    this._jointOffsetPreviewActive = false;
    this._ikSession = null;
    this.resetHistoryState();
    this.state.fileName = initialState.fileName;
    this.state.fileVersion = initialState.fileVersion;
    this.state.mappingVersion = initialState.mappingVersion;
    this.state.bonePropertiesVersion = initialState.bonePropertiesVersion;
    this.state.hasGltfScene = initialState.hasGltfScene;
    this.state.hasSkeleton = initialState.hasSkeleton;
    this.state.hasMapping = initialState.hasMapping;
    this.state.renderAssetInfo = null;
    this.state.chainSummaries = initialState.chainSummaries;
    this.state.presetName = initialState.presetName;
    this.state.loading = initialState.loading;
    this.state.error = initialState.error;
    this.state.warning = initialState.warning;
    this.state.visibility = { ...defaultVisibility };
    this.state.toolbarMenuMode = initialState.toolbarMenuMode;
    this.state.viewerToolMode = initialState.viewerToolMode;
    this.state.rotateSpaceMode = initialState.rotateSpaceMode;
    this.state.propertiesToolMode = initialState.propertiesToolMode;
    this.state.gridCellSize = initialState.gridCellSize;
    this.state.boneColorPreset = initialState.boneColorPreset;
    this.state.boneRadiusScale = initialState.boneRadiusScale;
    this.state.chainThicknessScale = initialState.chainThicknessScale;
    this.state.manipulatorScale = initialState.manipulatorScale;
    this.state.cameraFollowMotion = initialState.cameraFollowMotion;
    this.state.selection = initialState.selection;
    this.state.selectionVersion = 0;
    this.state.poseVersion = 0;
    this.state.excludedBoneNames = [];
    this.state.importWarningDetails = [];
    this.state.ikEnabled = false;
    this.state.ikSolveVersion = 0;
    this.state.ikEffectorIds = [];
    this.disposeGltfSceneSoon(previousScene);
  }

  getFile(): File | null {
    return this._file;
  }

  getGltfScene(): Group | null {
    return this._gltfScene;
  }

  getSkeleton(): Skeleton | null {
    return this._skeleton;
  }

  capturePoseSnapshot(): CartographerPoseSnapshot | null {
    const skeleton = this._skeleton;
    if (!skeleton) return null;
    return skeleton.bones.map((bone) => bone.poseLocal.clone());
  }

  restorePoseSnapshot(snapshot: CartographerPoseSnapshot | null) {
    const skeleton = this._skeleton;
    if (!skeleton || !snapshot) return;

    for (let index = 0; index < skeleton.bones.length; index++) {
      skeleton
        .bone(index)
        .poseLocal.copy(snapshot[index] ?? new THREE.Matrix4());
    }

    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  applyPreviewPoseLocals(
    poseLocals: THREE.Matrix4[],
    options: { notifyPoseVersion?: boolean } = {},
  ) {
    const skeleton = this._skeleton;
    if (!skeleton) return;
    const { notifyPoseVersion = true } = options;

    for (let index = 0; index < skeleton.bones.length; index++) {
      const nextPoseLocal = poseLocals[index];
      if (nextPoseLocal) {
        skeleton.bone(index).poseLocal.copy(nextPoseLocal);
        continue;
      }
      skeleton.bone(index).poseLocal.identity();
    }

    skeleton.computePosedWorldTransforms();
    if (notifyPoseVersion) {
      this.state.poseVersion = this.state.poseVersion + 1;
    }
  }

  getOutlinerHierarchy(): OutlinerHierarchy | null {
    return this._outlinerHierarchy;
  }

  setGltfScene(scene: Group | null) {
    const previousScene = this._gltfScene;
    this._gltfScene = scene;
    this.state.hasGltfScene = scene !== null;
    if (previousScene && previousScene !== scene) {
      this.disposeGltfSceneSoon(previousScene);
    }
  }

  setRenderAssetInfo(info: LoadedRenderAssetInfo | null) {
    this.state.renderAssetInfo = info;
  }

  setSkeleton(skeleton: Skeleton | null) {
    this._skeleton = skeleton;
    if (skeleton === null) {
      this._outlinerHierarchy = null;
      this.clearSelection();
    } else if (this._jointOffsetPreviewActive) {
      this.applyJointOffsetPreviewPose();
    }
    this.state.hasSkeleton = skeleton !== null;
  }

  setOutlinerHierarchy(hierarchy: OutlinerHierarchy | null) {
    this._outlinerHierarchy = hierarchy;
    if (
      this.state.selection?.kind === 'transform' &&
      (!hierarchy || !hierarchy.nodesById[this.state.selection.nodeId])
    ) {
      this.clearSelection();
    }
  }

  getMappingResult(): MappingResult | null {
    return this._mappingResult;
  }

  private syncMappingState(result: MappingResult | null) {
    this._mappingResult = result;
    this.state.hasMapping = result !== null;
    this.state.presetName = result?.presetName ?? null;

    if (result?.chainResults) {
      this.state.chainSummaries = Object.entries(result.chainResults).map(
        ([name, cr]) => ({
          name,
          boneNames: cr.boneNames,
          startBone: cr.startBone,
          endBone: cr.endBone,
          confidence: cr.confidence,
        }),
      );
    } else {
      this.state.chainSummaries = [];
    }

    if (result) {
      result.excludedBones = this.normalizeExcludedBoneNames(
        result.excludedBones,
      );
    }
    this.state.excludedBoneNames = [];

    if (result) {
      this.initIK();
    } else {
      this._ikSession = null;
      this.state.ikEnabled = false;
      this.state.ikEffectorIds = [];
      this.state.ikSolveVersion = this.state.ikSolveVersion + 1;
    }

    if (this._jointOffsetPreviewActive) {
      this.applyJointOffsetPreviewPose();
    }
  }

  private setMappingResultInternal(
    result: MappingResult | null,
    options?: { source?: string; mappingVersion?: number },
  ) {
    if (typeof options?.mappingVersion === 'number') {
      this.assignMappingVersion(
        options.mappingVersion,
        options.source ?? 'setMappingResult',
      );
    } else {
      this.bumpMappingVersion(options?.source ?? 'setMappingResult');
    }
    this.syncMappingState(result);
  }

  setMappingResult(result: MappingResult | null) {
    this.setMappingResultInternal(result);
  }

  /**
   * Bump mapping revision for persisted data changes that happen outside
   * MappingResult replacement (e.g. in-place bone property edits).
   */
  markMappingChanged(
    source = 'markMappingChanged',
    options?: { bumpBonePropertiesVersion?: boolean },
  ) {
    this.bumpMappingVersion(source);
    if (options?.bumpBonePropertiesVersion) {
      this.bumpBonePropertiesVersion();
    }
  }

  markBonePropertiesChanged(source = 'markBonePropertiesChanged') {
    this.markMappingChanged(source, { bumpBonePropertiesVersion: true });
  }

  setRecognitionWeightsInfo(weightsInfo: Map<number, boolean> | null) {
    this._recognitionWeightsInfo = weightsInfo ? new Map(weightsInfo) : null;
  }

  runMappingRecognition() {
    if (!this._skeleton) return;
    const result = recognizeSkeleton(
      this._skeleton,
      this._recognitionWeightsInfo,
    );
    this.setMappingResult(result);
  }

  clearMappingRecognition() {
    const mapping = this._mappingResult;
    if (!mapping) return;

    this.setMappingResult(
      new MappingResult({
        roleToBone: {},
        roleToConfidence: {},
        chains: {},
        chainResults: {},
        warnings: [],
        explain: {},
        presetName: mapping.presetName,
        presetId: mapping.presetId,
        detectedFeatures: [...mapping.detectedFeatures],
        excludedBones: [...mapping.excludedBones],
        candidates: {},
        roleToDetectionMethod: {},
        sideConvention: mapping.sideConvention,
        namingMode: mapping.namingMode,
        forwardAxis: mapping.forwardAxis,
        upAxis: mapping.upAxis,
        ioExtras: { ...mapping.ioExtras },
      }),
    );
  }

  selectBone(index: number | null) {
    this.setSelection(
      index === null ? null : { kind: 'bone', boneIndex: index },
      {
        incrementSelectionVersion: true,
      },
    );
  }

  selectTransformNode(nodeId: string | null) {
    this.setSelection(nodeId === null ? null : { kind: 'transform', nodeId }, {
      incrementSelectionVersion:
        getSelectedBoneIndex(this.state.selection) !== null,
    });
  }

  clearSelection() {
    this.setSelection(null, {
      incrementSelectionVersion:
        getSelectedBoneIndex(this.state.selection) !== null,
    });
  }

  setBonePoseTransform(index: number, transform: BonePoseTransform) {
    const skeleton = this._skeleton;
    if (!skeleton) return;
    if (index < 0 || index >= skeleton.bones.length) return;

    writeBonePoseTransform(skeleton.bone(index), transform);
    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  private setSelection(
    selection: CartographerSelection,
    options: { incrementSelectionVersion: boolean },
  ) {
    this.state.selection = selection;
    if (options.incrementSelectionVersion) {
      this.state.selectionVersion = this.state.selectionVersion + 1;
    }
  }

  resetBonePoseTransform(index: number) {
    const skeleton = this._skeleton;
    if (!skeleton) return;
    if (index < 0 || index >= skeleton.bones.length) return;

    skeleton.bone(index).poseLocal.identity();
    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  setBoneRoleOverride(boneName: string, nextRole: string | null) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedBoneName = boneName.trim();
    if (!trimmedBoneName) return;

    const trimmedNextRole = nextRole?.trim() ?? null;
    const { draft, chainResults } = this.createManualMappingDraft(mapping);
    let didChange = false;

    if (trimmedNextRole) {
      didChange = assignRoleToDraft(draft, trimmedBoneName, trimmedNextRole);
      didChange =
        syncRoleAssignmentDraft({
          draft,
          boneName: trimmedBoneName,
          roleName: trimmedNextRole,
          callbacks: this.createManualRoleChainSyncCallbacks(
            draft.chains,
            chainResults,
          ),
        }) || didChange;
    } else {
      didChange = clearRolesForBoneFromDraft(draft, trimmedBoneName);
    }

    if (!didChange) return;

    this.setMappingResult(
      this.buildOverrideMapping(mapping, {
        roleToBone: draft.roleToBone,
        roleToConfidence: draft.roleToConfidence,
        chains: draft.chains,
        chainResults,
      }),
    );
  }

  clearBoneChain(chainName: string) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedChainName = chainName.trim();
    if (!trimmedChainName) return;
    if (!mapping.chains[trimmedChainName]) return;

    const nextChains = { ...mapping.chains };
    const nextChainResults = { ...mapping.chainResults };
    delete nextChains[trimmedChainName];
    delete nextChainResults[trimmedChainName];

    this.setMappingResult(
      this.buildChainOverrideMapping(mapping, nextChains, nextChainResults),
    );
  }

  removeBoneFromChain(chainName: string, boneName: string) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedChainName = chainName.trim();
    const trimmedBoneName = boneName.trim();
    if (!trimmedChainName || !trimmedBoneName) return;

    const existingChain = mapping.chains[trimmedChainName] ?? [];
    if (!existingChain.includes(trimmedBoneName)) return;

    const nextChains = { ...mapping.chains };
    const nextChainResults = { ...mapping.chainResults };
    const nextChainBoneNames = existingChain.filter(
      (chainBoneName) => chainBoneName !== trimmedBoneName,
    );

    if (nextChainBoneNames.length === 0) {
      delete nextChains[trimmedChainName];
      delete nextChainResults[trimmedChainName];
    } else {
      nextChains[trimmedChainName] = nextChainBoneNames;
      nextChainResults[trimmedChainName] = this.buildChainMapping({
        chainName: trimmedChainName,
        chainBoneNames: nextChainBoneNames,
        existing: mapping.chainResults[trimmedChainName] ?? null,
      });
    }

    this.setMappingResult(
      this.buildChainOverrideMapping(mapping, nextChains, nextChainResults),
    );
  }

  setChainFromStartEnd(
    chainName: string,
    startBoneName: string,
    endBoneName: string,
  ) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedChainName = chainName.trim();
    const trimmedStartBoneName = startBoneName.trim();
    const trimmedEndBoneName = endBoneName.trim();
    if (!trimmedChainName || !trimmedStartBoneName || !trimmedEndBoneName)
      return;

    const chainBoneNames = this.buildShortestBonePath(
      trimmedStartBoneName,
      trimmedEndBoneName,
    );
    if (!chainBoneNames || chainBoneNames.length === 0) return;

    const { draft, chainResults } = this.createManualMappingDraft(mapping);
    let didChange = this.setChainDraftBones({
      chainName: trimmedChainName,
      chainBoneNames,
      chains: draft.chains,
      chainResults,
    });
    didChange =
      syncChainAssignmentDraft({
        draft,
        chainName: trimmedChainName,
      }) || didChange;

    if (!didChange) return;

    this.setMappingResult(
      this.buildOverrideMapping(mapping, {
        roleToBone: draft.roleToBone,
        roleToConfidence: draft.roleToConfidence,
        chains: draft.chains,
        chainResults,
      }),
    );
  }

  addBoneToChain(chainName: string, boneName: string) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedChainName = chainName.trim();
    const trimmedBoneName = boneName.trim();
    if (!trimmedChainName || !trimmedBoneName) return;

    const existingChain = mapping.chains[trimmedChainName] ?? [];
    if (existingChain.includes(trimmedBoneName)) return;

    // Add-to-chain is intentionally additive: include only the selected bone.
    const nextChainBoneNames = [...existingChain, trimmedBoneName];

    const nextChains = { ...mapping.chains };
    const nextChainResults = { ...mapping.chainResults };

    nextChains[trimmedChainName] = nextChainBoneNames;
    nextChainResults[trimmedChainName] = this.buildChainMapping({
      chainName: trimmedChainName,
      chainBoneNames: nextChainBoneNames,
      existing: mapping.chainResults[trimmedChainName] ?? null,
    });

    this.setMappingResult(
      this.buildChainOverrideMapping(mapping, nextChains, nextChainResults),
    );
  }

  setChainSolverType(chainName: string, solverType: string | null) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedChainName = chainName.trim();
    if (!trimmedChainName) return;

    const chainBoneNames = mapping.chains[trimmedChainName] ?? [];
    if (chainBoneNames.length === 0) return;

    const existingChainMapping = this.buildChainMapping({
      chainName: trimmedChainName,
      chainBoneNames,
      existing: mapping.chainResults[trimmedChainName] ?? null,
    });
    const nextSolverType = normalizeSolverType(trimmedChainName, solverType);
    const nextEffectiveSolverType = getEffectiveSolverType(
      trimmedChainName,
      nextSolverType,
    );
    const nextSolverProperties = normalizeSolverProperties(
      nextEffectiveSolverType,
      existingChainMapping.solverProperties,
    );
    if (
      existingChainMapping.solverType === nextSolverType &&
      areSolverPropertiesEqual(
        existingChainMapping.solverProperties,
        nextSolverProperties,
      )
    ) {
      return;
    }

    const nextChains = { ...mapping.chains };
    const nextChainResults = { ...mapping.chainResults };
    nextChainResults[trimmedChainName] = new ChainMapping({
      name: existingChainMapping.name,
      startBone: existingChainMapping.startBone,
      endBone: existingChainMapping.endBone,
      boneNames: existingChainMapping.boneNames,
      ikGoal: existingChainMapping.ikGoal,
      confidence: existingChainMapping.confidence,
      solverType: nextSolverType,
      solverParams: existingChainMapping.solverParams,
      solverProperties: nextSolverProperties,
      legType: existingChainMapping.legType,
    });

    this.setMappingResult(
      this.buildChainOverrideMapping(mapping, nextChains, nextChainResults),
    );
  }

  setChainSolverProperties(
    chainName: string,
    solverProperties: ChainSolverProperties | null,
  ) {
    const mapping = this._mappingResult;
    if (!mapping) return;

    const trimmedChainName = chainName.trim();
    if (!trimmedChainName) return;

    const chainBoneNames = mapping.chains[trimmedChainName] ?? [];
    if (chainBoneNames.length === 0) return;

    const existingChainMapping = this.buildChainMapping({
      chainName: trimmedChainName,
      chainBoneNames,
      existing: mapping.chainResults[trimmedChainName] ?? null,
    });
    const effectiveSolverType = getEffectiveSolverType(
      trimmedChainName,
      existingChainMapping.solverType,
    );
    const nextSolverProperties = normalizeSolverProperties(
      effectiveSolverType,
      solverProperties,
    );
    const current = existingChainMapping.solverProperties;
    if (areSolverPropertiesEqual(current, nextSolverProperties)) {
      return;
    }

    const nextChains = { ...mapping.chains };
    const nextChainResults = { ...mapping.chainResults };
    nextChainResults[trimmedChainName] = new ChainMapping({
      name: existingChainMapping.name,
      startBone: existingChainMapping.startBone,
      endBone: existingChainMapping.endBone,
      boneNames: existingChainMapping.boneNames,
      ikGoal: existingChainMapping.ikGoal,
      confidence: existingChainMapping.confidence,
      solverType: existingChainMapping.solverType,
      solverParams: existingChainMapping.solverParams,
      solverProperties: nextSolverProperties,
      legType: existingChainMapping.legType,
    });

    this.setMappingResult(
      this.buildChainOverrideMapping(mapping, nextChains, nextChainResults),
    );
  }

  toggleBoneExcluded(boneName: string) {
    const trimmedRef = boneName.trim();
    if (!trimmedRef) return;

    const mapping = this._mappingResult;
    if (mapping) {
      const current = [...mapping.excludedBones];
      const idx = current.findIndex((ref) => ref === trimmedRef);
      const isExcluding = idx < 0;
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(trimmedRef);
      }
      const excludedBones = this.normalizeExcludedBoneNames(current);

      if (!isExcluding) {
        this.setMappingResult(
          this.buildOverrideMapping(mapping, {
            excludedBones,
          }),
        );
        this.state.excludedBoneNames = [];
        return;
      }

      const { draft, chainResults } = this.createManualMappingDraft(mapping);
      const { didClearRoles, clearedChainNames } =
        clearBoneAssignmentsFromDraft(draft, trimmedRef);
      const didClearChains = clearedChainNames.length > 0;
      if (didClearChains) {
        this.rebuildDraftChainResults({
          chainNames: clearedChainNames,
          chains: draft.chains,
          chainResults,
        });
      }

      this.setMappingResult(
        this.buildOverrideMapping(mapping, {
          roleToBone: didClearRoles ? draft.roleToBone : undefined,
          roleToConfidence: didClearRoles ? draft.roleToConfidence : undefined,
          chains: didClearChains ? draft.chains : undefined,
          chainResults: didClearChains ? chainResults : undefined,
          excludedBones,
        }),
      );
      this.state.excludedBoneNames = [];
      return;
    }

    const current = [...this.state.excludedBoneNames];
    const idx = current.findIndex((ref) => ref === trimmedRef);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(trimmedRef);
    }
    this.state.excludedBoneNames = this.normalizeExcludedBoneNames(current);
    this.bumpMappingVersion('toggleBoneExcluded(state.excludedBoneNames)');
  }

  isBoneExcluded(boneName: string): boolean {
    if (
      this._mappingResult?.excludedBones.some(
        (excludedName) => excludedName === boneName,
      )
    ) {
      return true;
    }
    return this.state.excludedBoneNames.some(
      (excludedName) => excludedName === boneName,
    );
  }

  toggleVisibility(key: keyof ViewerVisibility) {
    this.state.visibility = {
      ...this.state.visibility,
      [key]: !this.state.visibility[key],
    };
  }

  setVisibility(key: keyof ViewerVisibility, visible: boolean) {
    if (this.state.visibility[key] === visible) return;
    this.state.visibility = {
      ...this.state.visibility,
      [key]: visible,
    };
  }

  setVisibilityValues(nextVisibility: Partial<ViewerVisibility>) {
    let changed = false;
    for (const [key, value] of Object.entries(nextVisibility) as [
      keyof ViewerVisibility,
      boolean | undefined,
    ][]) {
      if (value === undefined) continue;
      if (this.state.visibility[key] !== value) {
        changed = true;
        break;
      }
    }
    if (!changed) return;

    this.state.visibility = {
      ...this.state.visibility,
      ...nextVisibility,
    };
  }

  setToolbarMenuMode(mode: ToolbarMenuMode) {
    this.state.toolbarMenuMode = mode;
  }

  setViewerToolMode(mode: ViewerToolMode) {
    this.state.viewerToolMode = mode;
  }

  setRotateSpaceMode(mode: RotateSpaceMode) {
    this.state.rotateSpaceMode = mode;
  }

  setPropertiesToolMode(mode: PropertiesToolMode) {
    this.state.propertiesToolMode = mode;
  }

  setJointOffsetPreviewActive(active: boolean) {
    if (this._jointOffsetPreviewActive === active) return;
    this._jointOffsetPreviewActive = active;
    if (active) {
      this.applyJointOffsetPreviewPose();
      return;
    }
    this.clearJointOffsetPreviewPose();
  }

  setBoneRadiusScale(value: number) {
    this.state.boneRadiusScale = clampRenderScale(value);
  }

  setBoneColorPreset(preset: BoneColorPreset) {
    this.state.boneColorPreset = preset;
  }

  setChainThicknessScale(value: number) {
    this.state.chainThicknessScale = clampRenderScale(value);
  }

  setManipulatorScale(value: number) {
    this.state.manipulatorScale = clampRenderScale(value);
  }

  setCameraFollowMotion(enabled: boolean) {
    this.state.cameraFollowMotion = enabled;
  }

  applyBoneRotateFromWorldQuaternion(
    index: number,
    worldQuaternion: THREE.Quaternion,
  ) {
    const skeleton = this._skeleton;
    if (!skeleton) return;
    if (index < 0 || index >= skeleton.bones.length) return;

    const bone = skeleton.bone(index);
    const safeWorldQuaternion = normalizeQuaternion(worldQuaternion.clone());
    const currentWorldQuaternion = readQuaternionFromMatrix(bone.posedWorld);
    if (
      areQuaternionsNearlyEqual(currentWorldQuaternion, safeWorldQuaternion)
    ) {
      return;
    }

    const parentWorldMatrix =
      bone.parent === null
        ? skeleton.rootParentWorld
        : skeleton.bone(bone.parent).posedWorld;
    const parentWorldQuaternion = readQuaternionFromMatrix(parentWorldMatrix);
    const restLocalQuaternion = readQuaternionFromMatrix(bone.restLocal);
    const combinedLocalQuaternion = parentWorldQuaternion
      .clone()
      .invert()
      .multiply(safeWorldQuaternion);
    const nextPoseLocalQuaternion = restLocalQuaternion
      .clone()
      .invert()
      .multiply(combinedLocalQuaternion);

    writeBonePoseLocalQuaternion(bone, nextPoseLocalQuaternion);
    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  applyBoneRotateFromGimbalDelta(
    index: number,
    axis: GizmoRotationAxis,
    deltaDegrees: number,
  ) {
    if (!Number.isFinite(deltaDegrees)) return;
    if (Math.abs(deltaDegrees) < GIMBAL_DELTA_EPSILON_DEGREES) return;

    const skeleton = this._skeleton;
    if (!skeleton) return;
    if (index < 0 || index >= skeleton.bones.length) return;

    const axisIndex = GIMBAL_AXIS_INDEX[axis];
    if (axisIndex === undefined) return;

    const bone = skeleton.bone(index);
    const currentLocalMatrix = new THREE.Matrix4().multiplyMatrices(
      bone.restLocal,
      bone.poseLocal,
    );
    const currentLocalQuaternion = readQuaternionFromMatrix(currentLocalMatrix);
    const currentEulerDegrees = eulerDegreesFromQuaternion(
      currentLocalQuaternion,
      bone.eulerOrder,
    );

    const nextEulerDegrees: [number, number, number] = [
      currentEulerDegrees[0],
      currentEulerDegrees[1],
      currentEulerDegrees[2],
    ];
    nextEulerDegrees[axisIndex] += deltaDegrees;
    const nextLocalQuaternion = quaternionFromEulerDegrees(
      nextEulerDegrees,
      bone.eulerOrder,
    );
    if (
      areQuaternionsNearlyEqual(currentLocalQuaternion, nextLocalQuaternion)
    ) {
      return;
    }

    const restLocalQuaternion = readQuaternionFromMatrix(bone.restLocal);
    const nextPoseLocalQuaternion = restLocalQuaternion
      .clone()
      .invert()
      .multiply(nextLocalQuaternion);
    writeBonePoseLocalQuaternion(bone, nextPoseLocalQuaternion);
    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  setBoneEulerRotation(index: number, eulerDegrees: [number, number, number]) {
    if (
      !Number.isFinite(eulerDegrees[0]) ||
      !Number.isFinite(eulerDegrees[1]) ||
      !Number.isFinite(eulerDegrees[2])
    ) {
      return;
    }

    const skeleton = this._skeleton;
    if (!skeleton) return;
    if (index < 0 || index >= skeleton.bones.length) return;

    const bone = skeleton.bone(index);
    const nextLocalQuaternion = quaternionFromEulerDegrees(
      eulerDegrees,
      bone.eulerOrder,
    );
    const restLocalQuaternion = readQuaternionFromMatrix(bone.restLocal);
    const nextPoseLocalQuaternion = restLocalQuaternion
      .clone()
      .invert()
      .multiply(nextLocalQuaternion);
    writeBonePoseLocalQuaternion(bone, nextPoseLocalQuaternion);
    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  toggleToolbarMenu(mode: Exclude<ToolbarMenuMode, 'none'>) {
    this.state.toolbarMenuMode =
      this.state.toolbarMenuMode === mode ? 'none' : mode;
  }

  getIKSession(): IKSession | null {
    return this._ikSession;
  }

  initIK() {
    if (!this._skeleton || !this._mappingResult) return;
    this._ikSession = buildIKSession(this._skeleton, this._mappingResult);
    this.state.ikEffectorIds = [...this._ikSession.effectors.keys()];
    this.state.ikEnabled = true;
  }

  toggleIK() {
    this.recordHistory(
      this.state.ikEnabled ? 'Disable IK' : 'Enable IK',
      'ik',
      () => {
        if (!this.state.ikEnabled) {
          this.initIK();
          return;
        }
        this._ikSession?.resetAll();
        this._ikSession = null;
        this.state.ikEnabled = false;
        this.state.ikSolveVersion = this.state.ikSolveVersion + 1;
        this.state.poseVersion = this.state.poseVersion + 1;
        this.state.ikEffectorIds = [];
      },
    );
  }

  ikSetGoal(effectorId: EffectorId, position: THREE.Vector3) {
    this.recordHistory('Move IK goal', 'ik', () => {
      if (!this._ikSession) return;
      this._ikSession.setGoal(effectorId, position);
      this._ikSession.solve();
      this.state.ikSolveVersion = this.state.ikSolveVersion + 1;
      this.state.poseVersion = this.state.poseVersion + 1;
    });
  }

  ikResetEffector(effectorId: EffectorId) {
    this.recordHistory('Reset IK effector', 'ik', () => {
      if (!this._ikSession) return;
      this._ikSession.resetEffector(effectorId);
      this.state.ikSolveVersion = this.state.ikSolveVersion + 1;
      this.state.poseVersion = this.state.poseVersion + 1;
    });
  }

  ikResetAll() {
    this.recordHistory('Reset all IK effectors', 'ik', () => {
      if (!this._ikSession) return;
      this._ikSession.resetAll();
      this.state.ikSolveVersion = this.state.ikSolveVersion + 1;
      this.state.poseVersion = this.state.poseVersion + 1;
    });
  }

  exportMapping() {
    if (!this._mappingResult) return;
    const json = exportMappingJson(this._mappingResult, this._skeleton);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = this.state.fileName?.replace(/\.\w+$/, '') ?? 'mapping';
    a.download = `${baseName}_mapping.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Applies mapping + optional per-bone properties from an already-parsed import.
   * Used when loading queued imports or server-saved config after skeleton is ready.
   */
  applyImportedMapping(
    mapping: MappingResult,
    boneProperties: Record<string, BoneProperties>,
  ) {
    const skel = this._skeleton;
    if (!skel) {
      this.setMappingResult(mapping);
      return;
    }

    const importWarnings: string[] = [];
    const importWarningDetailsByRef = new Map<
      string,
      {
        reason: 'ambiguous' | 'unresolved';
        contexts: Set<string>;
      }
    >();
    const resolveImportedRef = (
      importedRef: string,
      context: string,
      options?: { warn?: boolean },
    ): string | null => {
      const trimmedRef = importedRef.trim();
      if (!trimmedRef) return null;
      const resolved = skel.resolveBoneRefWithStatus(trimmedRef, {
        namespace: 'either',
      });
      if (resolved.index !== null) {
        return skel.refForBoneIndex(resolved.index);
      }
      if (options?.warn === false) {
        return null;
      }
      const reason =
        resolved.status === 'ambiguous' ? 'ambiguous' : 'unresolved';
      const statusLabel = reason === 'ambiguous' ? 'Ambiguous' : 'Unresolved';
      importWarnings.push(
        `${statusLabel} bone ref '${trimmedRef}' in ${context}; entry skipped.`,
      );
      const existingDetail = importWarningDetailsByRef.get(trimmedRef);
      if (existingDetail) {
        if (existingDetail.reason === 'unresolved' && reason === 'ambiguous') {
          existingDetail.reason = 'ambiguous';
        }
        existingDetail.contexts.add(context);
      } else {
        importWarningDetailsByRef.set(trimmedRef, {
          reason,
          contexts: new Set([context]),
        });
      }
      return null;
    };

    const roleToBone: Record<string, string> = {};
    const roleToConfidence: Record<string, number> = {};
    for (const [role, importedRef] of Object.entries(mapping.roleToBone)) {
      const resolvedRef = resolveImportedRef(importedRef, `role '${role}'`);
      if (!resolvedRef) continue;
      roleToBone[role] = resolvedRef;
      const confidence = mapping.roleToConfidence[role];
      if (typeof confidence === 'number') roleToConfidence[role] = confidence;
    }

    const chains: Record<string, string[]> = {};
    const chainResults: Record<string, ChainMapping> = {};
    for (const [chainName, importedChainRefs] of Object.entries(
      mapping.chains,
    )) {
      const resolvedChainRefs = this.simplifyBonePath(
        importedChainRefs
          .map((ref) => resolveImportedRef(ref, `chain '${chainName}'`))
          .filter((ref): ref is string => ref !== null),
      );
      if (resolvedChainRefs.length === 0) {
        if (importedChainRefs.length > 0) {
          importWarnings.push(
            `Skipped chain '${chainName}' because none of its bone refs could be resolved.`,
          );
        }
        continue;
      }
      chains[chainName] = resolvedChainRefs;
      const existing = mapping.chainResults[chainName] ?? null;
      const base = this.buildChainMapping({
        chainName,
        chainBoneNames: resolvedChainRefs,
        existing,
      });
      const mappedIkGoal =
        existing?.ikGoal != null && existing.ikGoal !== ''
          ? resolveImportedRef(existing.ikGoal, `chain '${chainName}' ikGoal`)
          : null;
      chainResults[chainName] = new ChainMapping({
        name: base.name,
        startBone: base.startBone,
        endBone: base.endBone,
        boneNames: base.boneNames,
        ikGoal: mappedIkGoal,
        confidence: base.confidence,
        solverType: base.solverType,
        solverParams: base.solverParams,
        solverProperties: base.solverProperties,
        legType: base.legType,
      });
    }

    const excludedBones = this.normalizeExcludedBoneNames(
      mapping.excludedBones
        .map((ref) =>
          resolveImportedRef(ref, 'excluded_bones', { warn: false }),
        )
        .filter((ref): ref is string => ref !== null),
    );

    const ioExtras: Record<string, unknown> = { ...mapping.ioExtras };
    const jointOffsetsRaw = mapping.ioExtras[JOINT_OFFSETS_IO_KEY];
    if (
      jointOffsetsRaw &&
      typeof jointOffsetsRaw === 'object' &&
      !Array.isArray(jointOffsetsRaw)
    ) {
      const nextJointOffsets: Record<string, unknown> = {};
      for (const [importedRef, value] of Object.entries(
        jointOffsetsRaw as Record<string, unknown>,
      )) {
        const resolvedRef = resolveImportedRef(
          importedRef,
          'joint_offsets key',
        );
        if (!resolvedRef) continue;
        nextJointOffsets[resolvedRef] = value;
      }
      if (Object.keys(nextJointOffsets).length > 0) {
        ioExtras[JOINT_OFFSETS_IO_KEY] = nextJointOffsets;
      } else {
        delete ioExtras[JOINT_OFFSETS_IO_KEY];
      }
    }

    for (const [importedRef, props] of Object.entries(boneProperties)) {
      const resolvedRef = resolveImportedRef(
        importedRef,
        'bone_properties key',
      );
      if (!resolvedRef) continue;
      const index = skel.resolveBoneRef(resolvedRef, { namespace: 'internal' });
      if (index === null) continue;
      skel.bone(index).properties = props;
    }

    this.setMappingResult(
      new MappingResult({
        roleToBone,
        roleToConfidence,
        chains,
        chainResults,
        warnings: [...mapping.warnings, ...importWarnings],
        explain: mapping.explain,
        presetName: mapping.presetName,
        presetId: mapping.presetId,
        detectedFeatures: mapping.detectedFeatures,
        excludedBones,
        candidates: mapping.candidates,
        roleToDetectionMethod: mapping.roleToDetectionMethod,
        sideConvention: mapping.sideConvention,
        namingMode: mapping.namingMode,
        forwardAxis: mapping.forwardAxis,
        upAxis: mapping.upAxis,
        ioExtras,
      }),
    );

    const importWarningDetails = [...importWarningDetailsByRef.entries()]
      .map(([label, detail]) => ({
        label,
        reason: detail.reason,
        contexts: [...detail.contexts].sort(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (importWarnings.length > 0) {
      this.setImportWarnings(
        'Ambiguous or unresolved entries were skipped.',
        importWarningDetails,
      );
    } else {
      this.setWarning(null);
    }
  }

  applyImportedMappingFromJson(jsonStr: string) {
    const { mapping, boneProperties } = importMappingJson(jsonStr);
    this.applyImportedMapping(mapping, boneProperties);
  }

  importMapping(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        this.applyImportedMappingFromJson(reader.result as string);
      } catch (e) {
        this.setError(
          e instanceof Error ? e.message : 'Failed to import mapping',
        );
      }
    };
    reader.readAsText(file);
  }

  /**
   * Updates `joint_offsets` in mapping ioExtras keyed by skeleton bone ref.
   * Pass `rotation: null` to remove the offset for that bone ref.
   */
  setJointOffsetRotationForBone(
    boneRef: string,
    rotation: [number, number, number] | null,
  ) {
    const trimmedRef = boneRef.trim();
    const m = this._mappingResult;
    if (!m || !trimmedRef) return;

    const nextExtras = { ...m.ioExtras };
    const rawOffsets = nextExtras[JOINT_OFFSETS_IO_KEY];
    const offsets: Record<string, unknown> =
      rawOffsets && typeof rawOffsets === 'object' && !Array.isArray(rawOffsets)
        ? { ...(rawOffsets as Record<string, unknown>) }
        : {};

    if (rotation === null) {
      delete offsets[trimmedRef];
    } else {
      offsets[trimmedRef] = {
        rotation: [rotation[0], rotation[1], rotation[2]] as [
          number,
          number,
          number,
        ],
      };
    }

    if (Object.keys(offsets).length === 0) {
      delete nextExtras[JOINT_OFFSETS_IO_KEY];
    } else {
      nextExtras[JOINT_OFFSETS_IO_KEY] = offsets;
    }

    this.setMappingResult(
      new MappingResult({
        roleToBone: m.roleToBone,
        roleToConfidence: m.roleToConfidence,
        chains: m.chains,
        chainResults: m.chainResults,
        warnings: m.warnings,
        explain: m.explain,
        presetName: m.presetName,
        presetId: m.presetId,
        detectedFeatures: m.detectedFeatures,
        excludedBones: [...m.excludedBones],
        candidates: m.candidates,
        roleToDetectionMethod: m.roleToDetectionMethod,
        sideConvention: m.sideConvention,
        namingMode: m.namingMode,
        forwardAxis: m.forwardAxis,
        upAxis: m.upAxis,
        ioExtras: nextExtras,
      }),
    );
  }

  private applyJointOffsetPreviewPose() {
    const skeleton = this._skeleton;
    if (!skeleton) return;

    for (const bone of skeleton.bones) {
      bone.poseLocal.identity();
    }

    const mapping = this._mappingResult;
    if (mapping) {
      for (const [boneRef, rotation] of getJointOffsetRotationEntries(
        mapping.ioExtras,
      )) {
        const boneIndex = skeleton.resolveBoneRef(boneRef, {
          namespace: 'either',
        });
        if (boneIndex === null) continue;

        const bone = skeleton.bone(boneIndex);
        const nextQuaternion = quaternionFromEulerDegrees(
          [
            normalizeOffsetDegrees(rotation[0]),
            normalizeOffsetDegrees(rotation[1]),
            normalizeOffsetDegrees(rotation[2]),
          ],
          bone.eulerOrder,
        );
        writeBonePoseLocalQuaternion(bone, nextQuaternion);
      }
    }

    skeleton.computePosedWorldTransforms();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  private clearJointOffsetPreviewPose() {
    const skeleton = this._skeleton;
    if (!skeleton) return;
    skeleton.resetPoses();
    this.state.poseVersion = this.state.poseVersion + 1;
  }

  private createManualMappingDraft(mapping: MappingResult): {
    draft: ManualMappingDraft;
    chainResults: Record<string, ChainMapping>;
  } {
    const roleToBone = { ...mapping.roleToBone };
    const roleToConfidence = { ...mapping.roleToConfidence };
    const chains = { ...mapping.chains };

    return {
      draft: {
        roleToBone,
        roleToConfidence,
        chains,
      },
      chainResults: { ...mapping.chainResults },
    };
  }

  private createManualRoleChainSyncCallbacks(
    chains: Record<string, string[]>,
    chainResults: Record<string, ChainMapping>,
  ): ManualRoleChainSyncCallbacks {
    return {
      getShortestBonePath: (startBoneName, endBoneName) =>
        this.buildShortestBonePath(startBoneName, endBoneName),
      setChainBones: (chainName, chainBoneNames) =>
        this.setChainDraftBones({
          chainName,
          chainBoneNames,
          chains,
          chainResults,
        }),
    };
  }

  private buildOverrideMapping(
    mapping: MappingResult,
    overrides: {
      roleToBone?: Record<string, string>;
      roleToConfidence?: Record<string, number>;
      chains?: Record<string, string[]>;
      chainResults?: Record<string, ChainMapping>;
      excludedBones?: string[];
    },
  ): MappingResult {
    return new MappingResult({
      roleToBone: overrides.roleToBone ?? mapping.roleToBone,
      roleToConfidence: overrides.roleToConfidence ?? mapping.roleToConfidence,
      chains: overrides.chains ?? mapping.chains,
      chainResults: overrides.chainResults ?? mapping.chainResults,
      warnings: mapping.warnings,
      explain: mapping.explain,
      presetName: mapping.presetName,
      presetId: mapping.presetId,
      detectedFeatures: mapping.detectedFeatures,
      excludedBones: [...(overrides.excludedBones ?? mapping.excludedBones)],
      candidates: mapping.candidates,
      roleToDetectionMethod: mapping.roleToDetectionMethod,
      sideConvention: mapping.sideConvention,
      namingMode: mapping.namingMode,
      forwardAxis: mapping.forwardAxis,
      upAxis: mapping.upAxis,
      ioExtras: { ...mapping.ioExtras },
    });
  }

  private buildRoleOverrideMapping(
    mapping: MappingResult,
    roleToBone: Record<string, string>,
    roleToConfidence: Record<string, number>,
  ): MappingResult {
    return this.buildOverrideMapping(mapping, {
      roleToBone,
      roleToConfidence,
    });
  }

  private buildChainOverrideMapping(
    mapping: MappingResult,
    chains: Record<string, string[]>,
    chainResults: Record<string, ChainMapping>,
  ): MappingResult {
    return this.buildOverrideMapping(mapping, {
      chains,
      chainResults,
    });
  }

  setCharacterForwardAxis(forwardAxis: CharacterAxis) {
    const mapping = this._mappingResult;
    if (!mapping) return;
    if (mapping.forwardAxis === forwardAxis) return;
    this.setMappingResult(
      new MappingResult({
        roleToBone: mapping.roleToBone,
        roleToConfidence: mapping.roleToConfidence,
        chains: mapping.chains,
        chainResults: mapping.chainResults,
        warnings: mapping.warnings,
        explain: mapping.explain,
        presetName: mapping.presetName,
        presetId: mapping.presetId,
        detectedFeatures: mapping.detectedFeatures,
        excludedBones: [...mapping.excludedBones],
        candidates: mapping.candidates,
        roleToDetectionMethod: mapping.roleToDetectionMethod,
        sideConvention: mapping.sideConvention,
        namingMode: mapping.namingMode,
        forwardAxis,
        upAxis: mapping.upAxis,
        ioExtras: { ...mapping.ioExtras },
      }),
    );
  }

  setCharacterUpAxis(upAxis: CharacterAxis) {
    const mapping = this._mappingResult;
    if (!mapping) return;
    if (mapping.upAxis === upAxis) return;
    this.setMappingResult(
      new MappingResult({
        roleToBone: mapping.roleToBone,
        roleToConfidence: mapping.roleToConfidence,
        chains: mapping.chains,
        chainResults: mapping.chainResults,
        warnings: mapping.warnings,
        explain: mapping.explain,
        presetName: mapping.presetName,
        presetId: mapping.presetId,
        detectedFeatures: mapping.detectedFeatures,
        excludedBones: [...mapping.excludedBones],
        candidates: mapping.candidates,
        roleToDetectionMethod: mapping.roleToDetectionMethod,
        sideConvention: mapping.sideConvention,
        namingMode: mapping.namingMode,
        forwardAxis: mapping.forwardAxis,
        upAxis,
        ioExtras: { ...mapping.ioExtras },
      }),
    );
  }

  private buildChainMapping({
    chainName,
    chainBoneNames,
    existing,
  }: {
    chainName: string;
    chainBoneNames: string[];
    existing: ChainMapping | null;
  }): ChainMapping {
    const firstBoneName = chainBoneNames[0];
    const lastBoneName = chainBoneNames[chainBoneNames.length - 1];
    const normalizedSolverType = normalizeSolverType(
      chainName,
      existing?.solverType ?? null,
    );
    const effectiveSolverType = getEffectiveSolverType(
      chainName,
      normalizedSolverType,
    );
    return new ChainMapping({
      name: chainName,
      startBone: firstBoneName,
      endBone: lastBoneName,
      boneNames: chainBoneNames,
      ikGoal: existing?.ikGoal ?? null,
      confidence: existing?.confidence ?? 1,
      solverType: normalizedSolverType,
      solverParams: existing?.solverParams ?? {},
      solverProperties: normalizeSolverProperties(
        effectiveSolverType,
        existing?.solverProperties ?? null,
      ),
      legType: existing?.legType ?? null,
    });
  }

  private setChainDraftBones({
    chainName,
    chainBoneNames,
    chains,
    chainResults,
  }: {
    chainName: string;
    chainBoneNames: string[];
    chains: Record<string, string[]>;
    chainResults: Record<string, ChainMapping>;
  }): boolean {
    const nextChainBoneNames = [...chainBoneNames];
    const previousChainBoneNames = chains[chainName] ?? [];
    const previousChainResult = chainResults[chainName] ?? null;

    let didChange = this.removeBonesFromOtherChains({
      targetChainName: chainName,
      occupiedBoneNames: new Set(nextChainBoneNames),
      chains,
      chainResults,
    });

    if (!areStringArraysEqual(previousChainBoneNames, nextChainBoneNames)) {
      didChange = true;
    }

    if (
      previousChainResult === null ||
      previousChainResult.startBone !== nextChainBoneNames[0] ||
      previousChainResult.endBone !==
        nextChainBoneNames[nextChainBoneNames.length - 1] ||
      !areStringArraysEqual(previousChainResult.boneNames, nextChainBoneNames)
    ) {
      didChange = true;
    }

    chains[chainName] = nextChainBoneNames;
    chainResults[chainName] = this.buildChainMapping({
      chainName,
      chainBoneNames: nextChainBoneNames,
      existing: previousChainResult,
    });

    return didChange;
  }

  private removeBonesFromOtherChains({
    targetChainName,
    occupiedBoneNames,
    chains,
    chainResults,
  }: {
    targetChainName: string;
    occupiedBoneNames: Set<string>;
    chains: Record<string, string[]>;
    chainResults: Record<string, ChainMapping>;
  }): boolean {
    let didChange = false;

    for (const [chainName, chainBoneNames] of Object.entries(chains)) {
      if (chainName === targetChainName) continue;
      const filtered = chainBoneNames.filter(
        (chainBoneName) => !occupiedBoneNames.has(chainBoneName),
      );
      if (filtered.length === chainBoneNames.length) continue;
      didChange = true;

      if (filtered.length === 0) {
        delete chains[chainName];
        delete chainResults[chainName];
        continue;
      }

      chains[chainName] = filtered;
      chainResults[chainName] = this.buildChainMapping({
        chainName,
        chainBoneNames: filtered,
        existing: chainResults[chainName] ?? null,
      });
    }

    return didChange;
  }

  private rebuildDraftChainResults({
    chainNames,
    chains,
    chainResults,
  }: {
    chainNames: readonly string[];
    chains: Record<string, string[]>;
    chainResults: Record<string, ChainMapping>;
  }) {
    for (const chainName of chainNames) {
      const chainBoneNames = chains[chainName];
      if (!chainBoneNames || chainBoneNames.length === 0) {
        delete chainResults[chainName];
        continue;
      }

      chainResults[chainName] = this.buildChainMapping({
        chainName,
        chainBoneNames,
        existing: chainResults[chainName] ?? null,
      });
    }
  }

  private buildShortestBonePath(
    startBoneName: string,
    endBoneName: string,
  ): string[] | null {
    return buildShortestBonePathRefs(
      this._skeleton,
      startBoneName,
      endBoneName,
    );
  }

  private simplifyBonePath(path: string[]): string[] {
    const nextPath: string[] = [];
    const indexByBoneName = new Map<string, number>();
    for (const boneName of path) {
      const existingIndex = indexByBoneName.get(boneName);
      if (existingIndex === undefined) {
        nextPath.push(boneName);
        indexByBoneName.set(boneName, nextPath.length - 1);
        continue;
      }

      for (let i = nextPath.length - 1; i > existingIndex; i -= 1) {
        indexByBoneName.delete(nextPath[i]);
      }
      nextPath.length = existingIndex + 1;
    }
    return nextPath;
  }

  private normalizeExcludedBoneNames(boneNames: string[]): string[] {
    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const rawRef of boneNames) {
      const trimmedRef = rawRef.trim();
      if (!trimmedRef) continue;
      if (seen.has(trimmedRef)) continue;
      seen.add(trimmedRef);
      deduped.push(trimmedRef);
    }
    return deduped;
  }

  destroy() {
    // Full reset so remounts (route leave/return, React Strict Mode) never
    // reuse a half-disposed scene with stale flags like hasGltfScene.
    this.reset();
    super.destroy();
  }

  setError(message: string | null) {
    this.state.error = message;
  }

  setWarning(message: string | null) {
    this.state.warning = message;
    if (!message) {
      this.state.importWarningDetails = [];
    }
  }

  setImportWarnings(summary: string, details: ImportWarningDetail[]) {
    if (details.length === 0) {
      this.setWarning(null);
      return;
    }

    this.state.importWarningDetails = [...details].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    this.state.warning = formatImportWarningMessage(details.length, summary);
  }

  setLoading(loading: boolean) {
    this.state.loading = loading;
  }

  /** Updates header / export basename only (does not reload the model). */
  setDisplayFileName(name: string | null) {
    this.state.fileName = name;
  }

  setFile(file: File | null) {
    const previousScene = this._gltfScene;
    this._file = file;
    this._gltfScene = null;
    this._skeleton = null;
    this._outlinerHierarchy = null;
    this._mappingResult = null;
    this._recognitionWeightsInfo = null;
    this._ikSession = null;
    this.resetHistoryState();
    this.state.hasGltfScene = false;
    this.state.hasSkeleton = false;
    this.state.hasMapping = false;
    this.state.renderAssetInfo = null;
    this.state.chainSummaries = [];
    this.state.presetName = null;
    this.state.ikEnabled = false;
    this.state.ikSolveVersion = 0;
    this.state.poseVersion = 0;
    this.state.ikEffectorIds = [];
    this.state.toolbarMenuMode = 'none';
    this.state.excludedBoneNames = [];
    this.state.bonePropertiesVersion = 0;
    this.state.cameraFollowMotion = initialState.cameraFollowMotion;
    this.state.warning = null;
    this.state.importWarningDetails = [];
    this.state.fileName = file?.name ?? null;
    this.state.fileVersion = this.state.fileVersion + 1;
    this.assignMappingVersion(0, 'setFile(reset)');
    if (file) this.state.error = null;
    this.disposeGltfSceneSoon(previousScene);
  }

  private disposeGltfSceneSoon(scene: Group | null) {
    if (!scene) return;

    if (typeof window === 'undefined') {
      disposeCartographerObject3D(scene);
      return;
    }

    window.setTimeout(() => {
      disposeCartographerObject3D(scene);
    }, 0);
  }

  private logMappingVersionChange(
    source: string,
    previousMappingVersion: number,
  ) {
    console.log('[Cartographer][MappingVersion]', {
      source,
      previousMappingVersion,
      nextMappingVersion: this.state.mappingVersion,
      fileVersion: this.state.fileVersion,
      fileName: this.state.fileName,
      hasMapping: this.state.hasMapping,
    });
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: CartographerStore,
  displayName: 'CartographerStore',
  staticDeps: { global: globalStores },
});
